import {
  Component,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { Destination } from '@prlw/core/destinations/destination.entity';
import { DestinationsProvider } from '@prlw/core/destinations/destinations.provider';
import { Modifier, ModifierType } from '@prlw/core/monitor/types/common';
import { AllTableTabsComponent } from '../all-table-tabs/all-table-tabs.component';

const DEBOUNCE_TIME = 500;

type LabelName = 'basic' | 'simple' | 'prepaid' | 'defer';

@Component({
  selector: 'prlw-modifiers-ui',
  templateUrl: './modifiers.component.html',
  styleUrls: ['./modifiers.components.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModifiersComponent implements OnInit {
  public readonly BASIC_TAB_LABEL = 'basic';
  @ViewChild(AllTableTabsComponent) public tabs?: AllTableTabsComponent;
  @Input() public modifier: Modifier | null = null;
  public prepaidMargin = new UntypedFormControl();
  public deferPercent = new UntypedFormControl();
  public deferPeriod = new UntypedFormControl();
  @Output()
  public readonly prepaidMarginChange = this.prepaidMargin.valueChanges.pipe(
    debounceTime(DEBOUNCE_TIME),
  ) as EventEmitter<string>;
  @Output()
  public readonly deferChange = combineLatest([
    this.deferPercent.valueChanges,
    this.deferPeriod.valueChanges,
  ]).pipe(debounceTime(DEBOUNCE_TIME)) as unknown as EventEmitter<string[]>;

  constructor(private readonly destinationsProvider: DestinationsProvider) {}

  public ngOnInit(): void {
    if (this.modifier?.type === ModifierType.PREPAID) {
      this.prepaidMargin.setValue(this.modifier.data.prepaidMargin);
    }
    if (this.modifier?.type === ModifierType.DEFER) {
      this.deferPercent.setValue(this.modifier.data.deferPercent);
      this.deferPeriod.setValue(this.modifier.data.deferPeriod);
    }
  }

  public resetPrepaidMargin(): void {
    this.prepaidMargin.setValue(null);
  }

  public resetDeferPercent(): void {
    this.deferPercent.setValue(null);
  }

  public resetDeferPeriod(): void {
    this.deferPeriod.setValue(null);
  }

  public getLabel(label: LabelName): ModifierType {
    const labels: { [key: string]: ModifierType } = {
      basic: ModifierType.BASIC,
      simple: ModifierType.SIMPLE,
      prepaid: ModifierType.PREPAID,
      defer: ModifierType.DEFER,
    };
    return labels[label];
  }

  public isDisabled$(label: LabelName): Observable<boolean> {
    return this.destinationsProvider.currentDestination$.pipe(
      map(
        (destination: Destination | null) =>
          !destination && label !== ModifierType.BASIC,
      ),
    );
  }
}
